import React, { useEffect, useState } from "react";
import PinModal from "./PinModal";
import logo from "../img/logo2.webp";
import {
  ADMIN_TABS,
  PUBLIC_NAV_ITEMS,
  ADMIN_NAV_ITEMS,
} from "./adminTabsConfig";

const Sidebar = ({
  username,
  activeTab,
  handleTabClick,
  isSidebarOpen,
  setIsSidebarOpen,
  onLogout,
  restaurantOpen,
  onRestaurantToggle,
  loadingRestaurantStatus,
}) => {
  const ADMIN_PIN = process.env.REACT_APP_ADMIN_PIN;

  const [showPinModal, setShowPinModal] = useState(false);
  const [pinAction, setPinAction] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [sidebarView, setSidebarView] = useState(() => {
    const savedSidebarView = sessionStorage.getItem("admin_sidebarView");
    const savedActiveTab = sessionStorage.getItem("admin_activeTab");

    if (savedSidebarView) return savedSidebarView;
    if (ADMIN_TABS.includes(savedActiveTab)) return "admin";

    return "main";
  });

  useEffect(() => {
    sessionStorage.setItem("admin_sidebarView", sidebarView);
  }, [sidebarView]);

  useEffect(() => {
    if (ADMIN_TABS.includes(activeTab)) {
      setSidebarView("admin");
    }
  }, [activeTab]);

  const navButtonClass = (isActive) =>
    `flex w-full items-center rounded-xl text-left text-sm font-medium transition-all duration-200 ${
      isCollapsed ? "justify-center px-3 py-3" : "gap-3 px-4 py-3"
    } ${
      isActive
        ? "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30"
        : "text-slate-300 hover:bg-slate-800 hover:text-white"
    }`;

  const handleAdminAccessClick = () => {
    setPinAction("admin");
    setShowPinModal(true);
  };

  const handleRestaurantToggleClick = () => {
    setPinAction("restaurant-toggle");
    setShowPinModal(true);
  };

  const handlePinSuccess = async () => {
    if (pinAction === "admin") {
      setSidebarView("admin");
      return;
    }

    if (pinAction === "restaurant-toggle") {
      await onRestaurantToggle();
    }
  };

  const handleClosePinModal = () => {
    setShowPinModal(false);
    setPinAction(null);
  };

  const handleBackToMain = () => {
    setSidebarView("main");

    if (ADMIN_TABS.includes(activeTab)) {
      handleTabClick("orders");
    }
  };

  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex h-screen flex-col overflow-hidden bg-slate-900 text-white shadow-2xl transition-all duration-300 lg:static lg:translate-x-0 ${
          isCollapsed ? "w-24" : "w-52"
        } ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* HEADER */}
        <div
          className={`shrink-0 border-b border-slate-800 ${
            isCollapsed ? "px-3 py-4" : "px-5 py-5"
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div
              className={`flex min-w-0 items-center ${
                isCollapsed ? "justify-center w-full" : "gap-3"
              }`}
            >
              <img
                src={logo}
                alt="Lasgidi logo"
                className="h-10 w-10 rounded-lg object-contain"
              />

              {!isCollapsed && (
                <div className="min-w-0">
                  <h1 className="truncate text-xl font-bold tracking-tight">
                    Lasgidi
                  </h1>
                  <p className="text-sm text-slate-400">
                    {sidebarView === "main"
                      ? "Restaurant Admin"
                      : "Manager Menu"}
                  </p>
                </div>
              )}
            </div>

            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsCollapsed(true)}
                  className="hidden rounded-lg border border-slate-700 px-2 py-1 text-slate-400 transition hover:bg-slate-800 hover:text-white lg:block"
                >
                  <i className="fas fa-angle-left"></i>
                </button>

                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="text-slate-400 hover:text-white lg:hidden"
                >
                  ✕
                </button>
              </div>
            )}

            {isCollapsed && (
              <button
                onClick={() => setIsCollapsed(false)}
                className="absolute right-3 top-4 hidden rounded-lg border border-slate-700 px-2 py-1 text-slate-400 transition hover:bg-slate-800 hover:text-white lg:block"
              >
                <i className="fas fa-angle-right"></i>
              </button>
            )}
          </div>

          {/* RESTAURANT SWITCH */}
          {!isCollapsed && (
            <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-700 px-4 py-3">
              <span
                className={`text-sm font-semibold ${
                  restaurantOpen ? "text-emerald-400" : "text-slate-300"
                }`}
              >
                {restaurantOpen ? "Opened" : "Closed"}
              </span>

              <button
                disabled={loadingRestaurantStatus}
                onClick={handleRestaurantToggleClick}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                  loadingRestaurantStatus
                    ? "bg-slate-600 cursor-not-allowed"
                    : restaurantOpen
                      ? "bg-emerald-500"
                      : "bg-slate-500"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                    restaurantOpen ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          )}
        </div>

        {/* NAV AREA */}
        <div className="relative min-h-0 flex-1 overflow-hidden">
          <div
            className={`flex h-full w-[200%] transition-transform duration-300 ease-in-out ${
              sidebarView === "admin" ? "-translate-x-1/2" : "translate-x-0"
            }`}
          >
            {/* MAIN NAV */}
            <div
              className={`sidebar-scrollbar w-1/2 min-h-0 overflow-y-auto ${
                isCollapsed ? "px-2 py-4" : "px-4 py-5"
              }`}
            >
              <div className="space-y-2">
                {PUBLIC_NAV_ITEMS.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => handleTabClick(item.key)}
                    className={navButtonClass(activeTab === item.key)}
                    title={isCollapsed ? item.label : ""}
                  >
                    <span className="text-lg leading-none">{item.icon}</span>
                    {!isCollapsed && <span>{item.label}</span>}
                  </button>
                ))}
              </div>

              <div className="mt-3">
                <button
                  onClick={handleAdminAccessClick}
                  className={`flex w-full items-center rounded-xl border border-slate-700 bg-slate-800/70 text-sm text-slate-200 transition hover:bg-slate-800 hover:text-white ${
                    isCollapsed
                      ? "justify-center px-3 py-3"
                      : "gap-3 px-4 py-3"
                  }`}
                  title={isCollapsed ? "Manager Menu" : ""}
                >
                  <span className="text-lg leading-none">🔒</span>
                  {!isCollapsed && <span>Manager Menu</span>}
                </button>
              </div>
            </div>

            {/* ADMIN NAV */}
            <div
              className={`sidebar-scrollbar w-1/2 min-h-0 overflow-y-auto ${
                isCollapsed ? "px-2 py-4" : "px-4 py-5"
              }`}
            >
              <button
                onClick={handleBackToMain}
                className={`mb-4 flex w-full items-center rounded-xl border border-slate-700 bg-slate-800/70 text-sm text-slate-200 transition hover:bg-slate-800 hover:text-white ${
                  isCollapsed
                    ? "justify-center px-3 py-3"
                    : "gap-3 px-4 py-3"
                }`}
                title={isCollapsed ? "Back to Menu" : ""}
              >
                <span className="text-lg leading-none">⬅️</span>
                {!isCollapsed && <span>Back to Menu</span>}
              </button>

              <div className="space-y-2">
                {ADMIN_NAV_ITEMS.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => handleTabClick(item.key)}
                    className={navButtonClass(activeTab === item.key)}
                    title={isCollapsed ? item.label : ""}
                  >
                    <span className="text-lg leading-none">{item.icon}</span>
                    {!isCollapsed && <span>{item.label}</span>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div
          className={`shrink-0 border-t border-slate-800 ${
            isCollapsed ? "px-2 py-4" : "px-4 py-4"
          }`}
        >
          <button
            onClick={onLogout}
            className={`flex w-full items-center justify-center rounded-xl border border-red-500/40 bg-red-500/10 text-sm font-medium text-red-400 transition-all duration-200 hover:bg-red-500/20 hover:text-red-300 ${
              isCollapsed ? "px-3 py-3" : "gap-2 px-4 py-3"
            }`}
          >
            <span>↩</span>
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <PinModal
        isOpen={showPinModal}
        onClose={handleClosePinModal}
        onSuccess={handlePinSuccess}
        correctPin={ADMIN_PIN}
      />
    </>
  );
};

export default Sidebar;